import React, { useState, useEffect } from "react";
import { retrievePaginatedDogs } from "../api/retrievePaginatedDogs";
import { Dog } from "../types/dog";
import AdoptedDogCard from "../components/AdoptedDogCard";
import { Link } from "react-router-dom";

const AdoptedPage: React.FC = () => {
  const [dogs, setDogs] = useState<Dog[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchDogs = async () => {
      try {
        // /api/pets/dogs/ excludes adopted dogs, so ask the paginated
        // endpoint for them explicitly and collect every page.
        const params = { search: "", adoptable_status: ["adopted"] };
        const firstPage = await retrievePaginatedDogs(1, params);
        const pageSize = firstPage.results.length || 1;
        const totalPages = Math.ceil(firstPage.count / pageSize);
        const remainingPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            retrievePaginatedDogs(i + 2, params),
          ),
        );
        setDogs([
          ...firstPage.results,
          ...remainingPages.flatMap((page) => page.results),
        ]);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching dogs", error);
        setIsLoading(false);
      }
    };

    fetchDogs();
  }, []);

  if (isLoading) {
    return (
      <div>
        <div className="inset-0 z-50 flex h-750 items-center justify-center bg-white bg-opacity-75">
          <div className="flex flex-col items-center">
            <div className="loader mb-4 h-32 w-32 rounded-full border-8 border-t-8 border-blue ease-linear"></div>
            <h2 className="text-2xl font-semibold text-blue">Loading...</h2>
            <p className="text-blue">Please wait while we fetch these pups.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="mb-20 font-poppins text-gray-dark">
      <section className="w-full bg-gray-light px-6 py-12 md:px-0">
        <div className="flex h-full flex-col items-center justify-center gap-6">
          <h1 className="text-center text-3xl font-bold text-blue">
            Find your Pup
          </h1>
          <h2 className="text-center max-w-5xl">
            We love a happy ending. These dogs have found their families but lots of pups still need your support!
          </h2>
          <a href="/donate">
            <button className="rounded-lg bg-orange px-14 py-4 font-bold text-white shadow-lg hover:bg-orange-dark">
              Donate
            </button>
          </a>
        </div>
      </section>

      <div className="mt-8 text-center">
        {dogs ? (
          <h2 className="mb-4 text-3xl font-bold text-blue">Adopted!</h2>
        ) : (
          <h2 className="text-3xl font-bold text-blue">
            No dogs have been adopted yet!
          </h2>
        )}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3">
          {dogs.map((dog, index) => (
            <AdoptedDogCard key={index} dog={dog} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AdoptedPage;
